import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useAddPartner, useCodeManagerValues, useEditPartner } from "@/hooks/useLocalStorage";
import { AlertDialog } from "@/widgets/AlertDailog";
import { PartnerSchema, AddPartnerSchema, type PartnerForm, type AddPartnerForm } from "@/shared/partnerschema";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";

// ─── Field wrapper (mirrors CreateWorkflow) ───────────────────────────────────

function Field({
    label,
    required,
    error,
    children,
}: {
    label: string;
    required?: boolean;
    error?: string;
    children: React.ReactNode;
}) {
    return (
        <div className="space-y-1.5">
            <Label className="text-sm font-medium">
                {label}
                {required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            {children}
            {error && <p className="text-red-500 text-xs">{error}</p>}
        </div>
    );
}

// ─── Types ────────────────────────────────────────────────────────────────────

type AddPartnerProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    initialData?: PartnerForm | null;
};

// ─── Static options ───────────────────────────────────────────────────────────

const PARTNER_CATEGORY_OPTIONS = [
    { value: "Partners", label: "Partners" },
    { value: "Agencies", label: "Agencies" },
    { value: "Department", label: "Department" },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function AddPartner({ open, onOpenChange, initialData }: AddPartnerProps) {
    const isEdit = !!initialData;

    const addMutation = useAddPartner();
    const editMutation = useEditPartner();

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [reqStatus, setReqStatus] = useState(false);

    const { data: partnerTypeData } = useCodeManagerValues(undefined, "Partner Type");

    const partnerTypeOptions = (partnerTypeData ?? [])
        .filter((item: any) => item.entityType === "CODE")
        .map((item: any) => ({ value: String(item.id), label: item.name }));

    const {
        control,
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = useForm<PartnerForm | AddPartnerForm>({
        resolver: zodResolver(isEdit ? PartnerSchema : AddPartnerSchema),
        defaultValues: isEdit && initialData
            ? {
                ...initialData,
                RecordNumber: initialData.RecordNumber?.toString() || "",
                PartnerSubType: initialData.PartnerSubType?.toString() || "",
            }
            : {
                PartnerName: "",
                Description: "",
                PartnerType: "",
                PartnerSubType: "",
                RecordNumber: "",
                SelectedOptions: [],
                PermissionToDecrypt: false,
            },
    });

    // Reset form whenever the dialog opens / initialData changes
    useEffect(() => {
        if (!open) return;

        if (isEdit && initialData) {
            reset({
                ...initialData,
                RecordNumber: initialData.RecordNumber?.toString() || "",
                PartnerSubType: initialData.PartnerSubType?.toString() || "",
            });
        } else {
            reset({
                PartnerName: "",
                Description: "",
                PartnerType: "",
                PartnerSubType: "",
                RecordNumber: "",
                SelectedOptions: [],
                PermissionToDecrypt: false,
            });
        }
    }, [open, initialData]);

    const selectedPartnerType = watch("PartnerType");
    const isAgencyOrPartner =
        selectedPartnerType === "Agencies" || selectedPartnerType === "Partners";
    const isDepartment = selectedPartnerType === "Department";


    const onSubmit = (data: PartnerForm | AddPartnerForm) => {
        if (isEdit) {
            editMutation.mutate(data as PartnerForm);
        } else {
            addMutation.mutate(data as AddPartnerForm);
        }
    };

    useEffect(() => {
        if (addMutation.isSuccess) {
            setReqStatus(true);
            setAlertMessage("Partner Added Successfully!");
            setAlertOpen(true);
            onOpenChange(false);
            addMutation.reset();
        } else if (addMutation.isError) {
            const raw = addMutation.error as Error;
            setReqStatus(false);
            setAlertMessage(parseApiErrorMessage(raw.message));
            setAlertOpen(true);
            onOpenChange(false);
            addMutation.reset();
        }
    }, [addMutation.isSuccess, addMutation.isError]);

    useEffect(() => {
        if (editMutation.isSuccess) {
            setReqStatus(true);
            setAlertMessage("Partner Updated Successfully!");
            setAlertOpen(true);
            onOpenChange(false);
            editMutation.reset();
        } else if (editMutation.isError) {
            const raw = editMutation.error as Error;
            setReqStatus(false);
            setAlertMessage(parseApiErrorMessage(raw.message));
            setAlertOpen(true);
            onOpenChange(false);
            editMutation.reset();
        }
    }, [editMutation.isSuccess, editMutation.isError]);

    const isPending = addMutation.isPending || editMutation.isPending;

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {isEdit ? "Edit Partner" : "Add Partner"}
                        </DialogTitle>
                    </DialogHeader>

                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 pt-1">

                        {isEdit && <input type="hidden" {...register("ID" as any)} />}

                        <Field
                            label="Partner Category"
                            error={(errors as any).PartnerType?.message}
                        >
                            <Controller
                                name="PartnerType"
                                control={control}
                                render={({ field }) => (
                                    <Select value={field.value} onValueChange={field.onChange}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select Partner Category" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {PARTNER_CATEGORY_OPTIONS.map((opt) => (
                                                <SelectItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </Field>

                        {/* {isDepartment && (
                            <Field label="Permission to Decrypt">
                                <div className="flex items-center gap-2 pt-0.5">
                                    <Controller
                                        name="PermissionToDecrypt"
                                        control={control}
                                        render={({ field }) => (
                                            <Checkbox
                                                id="permissionToDecrypt"
                                                checked={!!field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        )}
                                    />
                                    <Label
                                        htmlFor="permissionToDecrypt"
                                        className="font-normal cursor-pointer"
                                    >
                                        Allow this department to decrypt records
                                    </Label>
                                </div>
                            </Field>
                        )} */}

                        <Field
                            label="Partner Name"
                            required
                            error={(errors as any).PartnerName?.message}
                        >
                            <Input
                                placeholder="Enter partner name"
                                {...register("PartnerName")}
                            />
                        </Field>

                        <Field
                            label="Description"
                            error={(errors as any).Description?.message}
                        >
                            <Textarea
                                placeholder="Enter partner description"
                                className="resize-none min-h-[90px]"
                                {...register("Description")}
                            />
                        </Field>

                        {isAgencyOrPartner && (
                            <>
                                <Field
                                    label="Partner Type"
                                    required
                                    error={(errors as any).PartnerSubType?.message}
                                >
                                    <Controller
                                        name="PartnerSubType"
                                        control={control}
                                        render={({ field }) => (
                                            <Select value={field.value as string} onValueChange={field.onChange}>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select Partner Type" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {partnerTypeOptions.map((opt) => (
                                                        <SelectItem key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </Field>

                                <Field
                                    label="Total Number"
                                    required
                                    error={(errors as any).RecordNumber?.message}
                                >
                                    <Input
                                        placeholder="Enter total number"
                                        type="number"
                                        {...register("RecordNumber")}
                                    />
                                </Field>
                            </>
                        )}

                        {/* Actions */}
                        <div className="flex justify-end gap-2 pt-1">
                            <Button type="submit" disabled={isPending}>
                                {isPending ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        {isEdit ? "Updating..." : "Saving..."}
                                    </>
                                ) : isEdit ? (
                                    "Update"
                                ) : (
                                    "Save"
                                )}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={isPending}
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />
        </>
    );
}